import { TFunction } from "i18next"
import { useWebNotificationsSubscription$data } from "@/hooks/__generated__/useWebNotificationsSubscription.graphql"
import { PATIENT_FILE_LOCK_REQUESTED } from "@/hooks/usePatientFileLockRequestNotificationHandler"
import { useUserStore } from "@/lib/fetch-users"
import { getPersistedLanguage } from "@/lib/language-storage"

export type NotificationMessageData = Partial<useWebNotificationsSubscription$data["webNotifications"]>

export type NotificationMessage = {
  title: string
  options: Omit<NotificationOptions, "data"> & {
    actions?: { action: string; title: string; icon?: string }[]
    data?: NotificationMessageData
  }
}

export const showNotification = async (data: useWebNotificationsSubscription$data, t: TFunction) => {
  if (!("Notification" in window)) {
    console.error("Browser does not support notifications.")
    return
  }

  const language = getPersistedLanguage()
  const {
    webNotifications: { kind, notificationId, patient, actionTriggeredBy },
  } = data

  const serviceWorkerRegistration = await navigator.serviceWorker.getRegistration()

  if (!serviceWorkerRegistration || !serviceWorkerRegistration.active) return

  // Get user data for the requestor from the user store
  const { users } = useUserStore.getState()
  const requestorUser = actionTriggeredBy && users ? users.get(actionTriggeredBy) : null

  // Noted: we might get in the future web notifications unrelated to a patient
  if (!patient || !requestorUser) {
    return
  }

  const newNotification: NotificationMessage = {
    title: t("notification.sofiaNotification"),
    options: { tag: notificationId, lang: language, data: data.webNotifications },
  }

  switch (kind) {
    case "APPOINTMENT_CONCLUSION_FAILED":
      {
        const message = t("notification.appointmentConclusionFailed.message", {
          patientFirstName: patient.firstName,
          patientLastName: patient.lastName,
        })
        newNotification.options.body = message
      }
      break
    case "PATIENT_FILE_LOCK_REQUESTED":
      {
        const message = t("notification.patientFileLockRequested.message", {
          requestorFirstName: requestorUser.firstName,
          requestorLastName: requestorUser.lastName,
          patientFirstName: patient.firstName,
          patientLastName: patient.lastName,
        })
        newNotification.options.body = message
        newNotification.options.actions = [
          { action: PATIENT_FILE_LOCK_REQUESTED.ALLOW, title: t("notification.patientFileLockRequested.allow") },
          { action: PATIENT_FILE_LOCK_REQUESTED.DENY, title: t("notification.patientFileLockRequested.deny") },
        ]
        newNotification.options.requireInteraction = true
      }
      break
    case "PATIENT_FILE_LOCK_TAKEN":
      {
        const message = t("notification.patientFileLockTaken.message", {
          requestorFirstName: requestorUser.firstName,
          requestorLastName: requestorUser.lastName,
          patientFirstName: patient.firstName,
          patientLastName: patient.lastName,
        })
        newNotification.options.body = message
      }
      break
  }

  return serviceWorkerRegistration.active.postMessage(newNotification)
}
